(ns ui.view.counterparty-diligence.responder-form.sub
  (:require
   [common.util :as cu]
   [re-frame.core :as rf]
   [ui.view.counterparty-diligence.responder-form.model :as model]
   [common.schema.entities.counterparty-diligence.templates.validation :as validation]
   [clojure.string :as string]))

(rf/reg-sub
 ::state
 (fn [db [_ {:keys [state-path]}]]
   (get-in db state-path)))

(defn state-extractor
  [[_ ctx]]
  [(rf/subscribe [::state ctx])])

(rf/reg-sub
 ::loading?
 state-extractor
 (fn [[state]]
   (or (nil? state)
       (get-in state model/loading-path))))

(rf/reg-sub
 ::submitting?
 state-extractor
 (fn [[state]]
   (get-in state model/submitting-path)))

(rf/reg-sub
 ::view-mode
 state-extractor
 (fn [[state]]
   (get-in state model/view-mode-path)))

(rf/reg-sub
 ::read-only?
 state-extractor
 (fn [[state]]
   (= (get-in state model/view-mode-path) :form-read-only)))

(rf/reg-sub
 ::tenant-user-access?
 state-extractor
 (fn [[state]]
   (get-in state model/tenant-user-access-path)))

(rf/reg-sub
 ::submitted?
 state-extractor
 (fn [[state]]
   (get-in state model/submitted-path)))

(rf/reg-sub
 ::template
 state-extractor
 (fn [[state]]
   (get-in state model/template-path)))

(rf/reg-sub
 ::language
 state-extractor
 (fn [[state]]
   (get-in state model/language-path)))

(rf/reg-sub
 ::e-signature-required?
 state-extractor
 (fn [[state]]
   (get-in state model/e-signature-required-path)))

(rf/reg-sub
 ::guest-access-only?
 state-extractor
 (fn [[state]]
   (let [tu-access? (get-in state model/tenant-user-access-path)]
     (not tu-access?))))

(rf/reg-sub
 ::form-available?
 state-extractor
 (fn [[state]]
   (get-in state model/form-available-path)))

(rf/reg-sub
 ::date-format
 state-extractor
 (fn [[state]]
   (get-in state model/date-format-path)))

(rf/reg-sub
 ::title
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])
    (rf/subscribe [::language ctx])])
 (fn [[template lang]]
   (get-in template [:template/form-title lang])))

(rf/reg-sub
 ::form-preface-content
 (fn [[_ ctx]]
   [(rf/subscribe [::template ctx])
    (rf/subscribe [::language ctx])])
 (fn [[template lang]]
   (get-in template [:template/form-preface lang])))

(rf/reg-sub
 ::attachments
 state-extractor
 (fn [[state]]
   (get-in state model/attachments-path)))

(rf/reg-sub
 ::field-attachments
 (fn [[_ ctx]]
   [(rf/subscribe [::attachments ctx])])
 (fn [[attachments] [_ {:keys [attachment-ids]}]]
   (when attachment-ids
     (->> attachment-ids
          (remove cu/blank-value?)
          (mapv #(get attachments %))))))

(rf/reg-sub
 ::attachment-uploads
 state-extractor
 (fn [[state]]
   (get-in state model/attachment-uploads-path)))

(rf/reg-sub
 ::attachment-uploads-by-field
 (fn [[_ ctx]]
   [(rf/subscribe [::attachment-uploads ctx])])
 (fn [[attachment-uploads]]
   (->> attachment-uploads
        vals
        (group-by (fn [upload]
                    (:field upload))))))

(rf/reg-sub
 ::field-attachment-uploads
 (fn [[_ ctx]]
   [(rf/subscribe [::attachment-uploads-by-field ctx])])
 (fn [[uploads-by-field] [_ {:keys [field]}]]
   (get uploads-by-field field)))

(rf/reg-sub
 ::answers
 state-extractor
 (fn [[state]]
   (get-in state model/answers-path {})))

(rf/reg-sub
 ::initial-answers
 state-extractor
 (fn [[state]]
   (get state :initial-answers)))

(rf/reg-sub
 ::unsaved-changes?
 (fn [[_ ctx]]
   [(rf/subscribe [::answers ctx])
    (rf/subscribe [::initial-answers ctx])])
 (fn [[answers initial-answers]]
   (model/unsaved-changes?
    {:answers answers
     :initial-answers initial-answers})))

(rf/reg-sub
 ::saving-answers?
 state-extractor
 (fn [[state]]
   (get-in state model/saving-answers-path)))

(rf/reg-sub
 ::field-value
 (fn [[_ ctx]]
   [(rf/subscribe [::answers ctx])])
 (fn [[answers] [_ {:keys [field] :as _args}]]
   (get answers field)))

(defn- person-identity-key
  "Dedup key matching the screening logic: first-names + last-name + DOB +
   nationality, all normalized. Returns nil when any component is blank — a
   person with incomplete identity data should not dedup against others."
  [person]
  (let [first-names (some-> (:person/first-names person) cu/str-trim-lcase)
        last-name   (some-> (:person/last-name person) cu/str-trim-lcase)
        dob         (some-> (:person/date-of-birth person) str string/trim not-empty)
        nationality (let [n (:person/nationality person)]
                      (cond
                        (keyword? n) (name n)
                        (string? n)  (cu/str-trim-lcase n)))]
    (when (and (not (string/blank? first-names))
               (not (string/blank? last-name))
               (not (string/blank? dob))
               (not (string/blank? nationality)))
      [first-names last-name dob nationality])))

(rf/reg-sub
 ::existing-person-identity-keys
 (fn [[_ ctx]]
   [(rf/subscribe [::field-value ctx])])
 (fn [[entities] _]
   (->> entities
        (keep person-identity-key)
        set)))

(rf/reg-sub
 ::already-ubo?
 (fn [[_ {:keys [state-path target-field]}]]
   [(rf/subscribe [::existing-person-identity-keys
                   {:state-path state-path :field target-field}])])
 (fn [[existing-keys] [_ {:keys [row-data]}]]
   (let [row-key (person-identity-key row-data)]
     (boolean (and row-key (contains? existing-keys row-key))))))

(rf/reg-sub
 ::signatories
 (fn [[_ ctx]]
   [(rf/subscribe [::answers ctx])])
 (fn [[answers]]
   (-> answers
       :signatories)))

(rf/reg-sub
 ::signatories-missing-data?
 (fn [[_ ctx]]
   [(rf/subscribe [::e-signature-required? ctx])
    (rf/subscribe [::signatories ctx])])
 (fn [[e-signature-required? signatories]]
   (and e-signature-required?
        (->> signatories
             (some (fn [{:keys [full-name email]}]
                     (let [full-name (string/trim (or full-name ""))
                           email (string/trim (or email ""))]
                       (or (cu/empty-value? full-name)
                           (cu/empty-value? email)))))))))

(rf/reg-sub
 ::touched-fields
 state-extractor
 (fn [[state]]
   (get-in state model/touched-fields-path #{})))

(rf/reg-sub
 ::all-field-errors
 state-extractor
 (fn [[state]]
   (get-in state model/field-errors-path)))

(rf/reg-sub
 ::show-all-field-errors?
 state-extractor
 (fn [[state]]
   (get-in state model/show-all-field-errors-path)))

(rf/reg-sub
 ::hide-all-field-errors?
 state-extractor
 (fn [[state]]
   (get-in state model/hide-all-field-errors-path)))

(rf/reg-sub
 ::field-errors
 (fn [[_ ctx]]
   [(rf/subscribe [::all-field-errors ctx])])
 (fn [[field-errors] [_ {:keys [field] :as _ctx}]]
   (get field-errors field)))

(rf/reg-sub
 ::show-field-errors?
 (fn [[_ ctx]]
   [(rf/subscribe [::touched-fields ctx])
    (rf/subscribe [::show-all-field-errors? ctx])
    (rf/subscribe [::hide-all-field-errors? ctx])])
 (fn [[touched-fields show-all-field-errors? hide-all-field-errors?]
      [_ {:keys [field]}]]
   (and (not hide-all-field-errors?)
        (or show-all-field-errors?
            (contains? touched-fields field)))))

(rf/reg-sub
 ::visible-fields
 state-extractor
 (fn [[state]]
   (get-in state model/visible-fields-path #{})))

;; Testing
#_(rf/reg-sub
   ::field-error-show-label
   state-extractor
   (fn [[state]]
     (let [show-all? (get-in state model/show-all-field-errors-path)
           hide-all? (get-in state model/hide-all-field-errors-path)]
       (cond show-all? "Showing all field errors"
             hide-all? "Hiding all field errors"
             :else "Showing field errors if touched"))))

(rf/reg-sub
 ::confirmation-modal-state
 state-extractor
 (fn [[state]]
   (get-in state model/confirmation-modal-state-path)))
